// 프레임워크 중립 design token. SoT = owma `apps/owma/src/scss/abstracts/_color.scss`.
// (B) 어댑터가 이 토큰을 antd ConfigProvider(현재) / shadcn CSS vars(2단계)로 매핑한다.
// → 소비자·(A) 는 antd theme 타입을 모른다.

export interface OmwCommonTheme {
  colorPrimary: string
  colorSuccess: string
  colorWarning: string
  colorError: string
  colorTextPrimary: string
  colorTextSecondary: string
  borderRadius: number
  fontFamily: string
}

// owma `_color.scss` 추출 기본 토큰.
export const omwCommonTheme: OmwCommonTheme = {
  colorPrimary: '#0077ff', // --primary(#07f)
  colorSuccess: '#10bf79', // --accent
  colorWarning: '#ffa466', // --warning
  colorError: '#ff615a', // --danger
  colorTextPrimary: '#262626', // --primary-text
  colorTextSecondary: '#6b7280', // --secondary-text
  borderRadius: 8, // owma 시각 파리티(입력/카드/버튼 radius 8)
  fontFamily:
    '"Pretendard GOV Variable", "Pretendard GOV", -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Segoe UI", Roboto, sans-serif',
}

// owma 시각 파리티용 시맨틱 팔레트(값 = `_color.scss` 추출).
// 컴포넌트는 inline/scss 에서 `var(--om-*, #fallback)` 로 소비 → ThemeProvider 가 :root 주입.
export interface OmwPalette {
  primary: string
  primaryStrong: string
  primaryBg: string
  textStrongest: string
  textPrimary: string
  textStrong: string
  textSecondary: string
  textTertiary: string
  border: string
  borderStrong: string
  surface: string
  bgPage: string
  bgSubtle: string
  bgInfo: string
  danger: string
  dangerBg: string
  success: string
  warning: string
  warningText: string
  focusRing: string
}

export const owmaPalette: OmwPalette = {
  primary: '#0077ff', // --primary
  primaryStrong: '#2341e2', // --blue-700
  primaryBg: '#e3eefb', // 선택 항목 배경
  textStrongest: '#111827', // --gray-900
  textPrimary: '#262626', // --primary-text
  textStrong: '#374151', // --gray-700
  textSecondary: '#6b7280', // --secondary-text
  textTertiary: '#9ca3af', // --gray-400
  border: '#e5e7eb', // --gray-200 / --light-border
  borderStrong: '#d1d5db', // --gray-300 / --border
  surface: '#ffffff', // --white
  bgPage: '#f9fafb', // --gray-50
  bgSubtle: '#f3f4f6', // --gray-100
  bgInfo: '#d8e4ff', // --blue-100
  danger: '#ff615a', // --danger
  dangerBg: '#fae8e7', // --sub-light-red
  success: '#10bf79', // --accent
  warning: '#ffa466', // --warning
  warningText: '#ff9045', // --light-warning
  focusRing: '#91caff', // 키보드 포커스 링(antd colorPrimaryBorder 계열 — omButton 기존 값과 통일)
}

// OmwPalette → `--om-*` CSS 변수 맵. ThemeProvider 가 :root 에 주입, 컴포넌트는 var() 로 참조.
export function paletteCssVars(p: OmwPalette): Record<string, string> {
  return {
    '--om-primary': p.primary,
    '--om-primary-strong': p.primaryStrong,
    '--om-primary-bg': p.primaryBg,
    '--om-text-strongest': p.textStrongest,
    '--om-text-primary': p.textPrimary,
    '--om-text-strong': p.textStrong,
    '--om-text-secondary': p.textSecondary,
    '--om-text-tertiary': p.textTertiary,
    '--om-border': p.border,
    '--om-border-strong': p.borderStrong,
    '--om-surface': p.surface,
    '--om-bg-page': p.bgPage,
    '--om-bg-subtle': p.bgSubtle,
    '--om-bg-info': p.bgInfo,
    '--om-danger': p.danger,
    '--om-danger-bg': p.dangerBg,
    '--om-success': p.success,
    '--om-warning': p.warning,
    '--om-warning-text': p.warningText,
    '--om-focus-ring': p.focusRing,
  }
}
